import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'

interface LegacyBuildingRedirectProps {
  children: React.ReactNode
}

function isNumericId(id?: string): boolean {
  if (!id) return false
  const n = Number(id)
  return Number.isFinite(n) && String(n) === id
}

export function LegacyBuildingRedirect({ children }: LegacyBuildingRedirectProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [resolving, setResolving] = useState(false)

  const needsResolve = useMemo(() => !!id && !isNumericId(id), [id])

  useEffect(() => {
    let cancelled = false
    async function resolveAndRedirect(uuid: string) {
      try {
        setResolving(true)
        const { data, error } = await supabase.functions.invoke('resolve-building-id', {
          body: { id: uuid },
        })
        if (error) throw error
        const newId = data?.new_id
        if (!cancelled && newId) {
          const target = location.pathname.replace(`/buildings/${uuid}`, `/buildings/${newId}`)
          navigate(target + location.search + location.hash, { replace: true })
        }
      } catch (e) {
        // If resolution fails, just let the page render and likely 404 gracefully
        console.error('Legacy building ID resolution failed:', e)
      } finally {
        if (!cancelled) setResolving(false)
      }
    }

    if (needsResolve && id) {
      resolveAndRedirect(id)
    }
    return () => {
      cancelled = true
    }
  }, [needsResolve, id, navigate, location.pathname, location.search, location.hash])

  if (needsResolve || resolving) {
    return null
  }
  return <>{children}</>
}
